import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, User, CheckCircle, PlayCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ChallengeTask {
  id: string;
  challenge_id: string;
  title: string;
  description: string | null;
  estimated_hours: number | null;
  difficulty: "easy" | "medium" | "hard";
  skill_tags: string[];
  status: "open" | "claimed" | "in_review" | "complete";
  order_index: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  claim?: {
    id: string;
    user_id: string;
    status: string;
    user_email?: string;
  } | null;
}

interface TaskCardProps {
  task: ChallengeTask;
  currentUserId: string | null;
  canManage: boolean;
  onClaim: (taskId: string) => void;
  onUnclaim: (taskId: string) => void;
  onSubmit: (taskId: string) => void;
  onApprove: (taskId: string) => void;
  onReject: (taskId: string) => void;
  onEdit?: (task: ChallengeTask) => void;
  claiming?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  currentUserId,
  canManage,
  onClaim,
  onUnclaim,
  onSubmit,
  onApprove,
  onReject,
  onEdit,
  claiming,
}) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-500/20 text-green-600 border-green-500/30";
      case "medium":
        return "bg-yellow-500/20 text-yellow-600 border-yellow-500/30";
      case "hard":
        return "bg-red-500/20 text-red-600 border-red-500/30";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "bg-blue-500/20 text-blue-600";
      case "claimed":
        return "bg-purple-500/20 text-purple-600";
      case "in_review":
        return "bg-orange-500/20 text-orange-600";
      case "complete":
        return "bg-green-500/20 text-green-600";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const isClaimedByMe = task.claim?.user_id === currentUserId;
  const canClaimTask = task.status === "open" && currentUserId && !claiming;

  return (
    <Card
      className={cn(
        "transition-all hover:shadow-md",
        task.status === "complete" && "opacity-70"
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge className={getStatusColor(task.status)} variant="outline">
                {task.status.replace("_", " ")}
              </Badge>
              <Badge className={getDifficultyColor(task.difficulty)} variant="outline">
                {task.difficulty}
              </Badge>
            </div>

            <h4 className="font-medium text-foreground truncate">{task.title}</h4>

            {task.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {task.description}
              </p>
            )}

            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
              {task.estimated_hours && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {task.estimated_hours}h
                </span>
              )}
              {task.claim?.user_email && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {task.claim.user_email}
                </span>
              )}
            </div>

            {task.skill_tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {task.skill_tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {/* Claim button for open tasks */}
            {canClaimTask && (
              <Button
                size="sm"
                onClick={() => onClaim(task.id)}
                disabled={claiming}
              >
                <PlayCircle className="h-4 w-4 mr-1" />
                Claim
              </Button>
            )}

            {/* Actions for claimed task by current user */}
            {isClaimedByMe && task.status === "claimed" && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => onSubmit(task.id)}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Submit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onUnclaim(task.id)}
                >
                  Unclaim
                </Button>
              </>
            )}

            {/* Review actions for team leads */}
            {canManage && task.status === "in_review" && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => onApprove(task.id)}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReject(task.id)}
                >
                  Reject
                </Button>
              </>
            )}

            {/* Edit button for managers */}
            {canManage && onEdit && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onEdit(task)}
              >
                Edit
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TaskCard;
